import React, { useMemo, useState, useRef, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry } from 'ag-grid-community';
import { AllCommunityModule } from 'ag-grid-community';
import { getAuthHeaders } from '../../utils/authUtils';

ModuleRegistry.registerModules([AllCommunityModule]);

const fieldLabels = {
  origin: 'Origin',
  destination: 'Destination',
  opcarrier: 'Op. By',
  mktcarrier: 'Carrier',
  effdt: 'Date',
  fltnum: 'Flt #',
  fleet: 'Aircraft Type',
  deptime: 'Dep. Time',
  arvtime: 'Arr. Time',
  numflights: '# of Flights',
  dep_count: 'Total Flights',
  origin_name: 'Origin Name',
  destination_name: 'Destination Name',
  destination_country: 'Destination Country',
  destination_region: 'Destination Region',
  origin_name: 'Origin Name',
  origin_country: 'Origin Country',
  origin_region: 'Origin Region',
  mktcarrier_name: 'Carrier Name',
  opcarrier_name: 'Op. By Name',
  totSeats: 'Seats',
  dupleg: 'Code Share',
  cs_flights: 'Code Share Flights',
  flight_time: 'Flight Time', 
};

const isNumericField = (field, data) => {
  return data.some(row => {
    const value = row[field]; 
    if(value !== undefined && value !== null && value !== '' && !isNaN(Number(value))){
      return true;
    }
    return false;
  });
};

const DataTable = ({ 
  data, 
  selectedFields, 
  loading, 
  totalRecords, 
  returnedRecords, 
  hasMore, 
  isLimited, 
  limit, 
  loadAll,
  onLoadAll,
  downloadUrl,
  userId,
}) => {
  const gridRef = useRef();
  const filterDisplayRef = useRef(null);
  const [loadingAll, setLoadingAll] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [gridApi, setGridApi] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const excludedNumericFields = ['fltnum', 'fleet'];
  const includeTimeFields = ['flight_time'];

  const numericFields = useMemo(() => {
    return selectedFields.filter(field => !excludedNumericFields.includes(field) && isNumericField(field, data));
  }, [selectedFields, data]);

  const timeFields = useMemo(() => { 
    return selectedFields.filter(field => includeTimeFields.includes(field));
  }, [selectedFields]);

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  // Handle window resize to detect mobile/desktop
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update filter display without causing re-renders
  const onFilterChanged = () => {
    if (!gridRef.current || !filterDisplayRef.current) return;
    
    const filterModel = gridRef.current.api.getFilterModel();
    const filterCount = Object.keys(filterModel).length;
    
    if (filterCount === 0) {
      filterDisplayRef.current.style.display = 'none';
    } else {
      filterDisplayRef.current.style.display = 'block';
      
      // Update count
      const countElement = filterDisplayRef.current.querySelector('.filter-count');
      if (countElement) {
        countElement.textContent = `${filterCount} filter(s) active`;
      }
      
      // Update badges
      const badgesContainer = filterDisplayRef.current.querySelector('.filter-badges');
      if (badgesContainer) {
        badgesContainer.innerHTML = '';
        
        Object.entries(filterModel).forEach(([field, filter]) => {
          let displayText = '';
          
          if (filter.type) {
            displayText = filter.type;
            if (filter.filter !== undefined && filter.filter !== null) {
              displayText += ` "${filter.filter}"`;
            }
            if (filter.filterTo !== undefined && filter.filterTo !== null) {
              displayText = `${filter.filter || 0}-${filter.filterTo}`;
            }
          } else if (filter.filterType === 'set' && filter.values) {
            displayText = `in (${filter.values.length} selected)`;
          }
          
          const badge = document.createElement('span');
          badge.className = 'badge bg-info me-2';
          badge.textContent = `${fieldLabels[field] || field}: ${displayText}`;
          badgesContainer.appendChild(badge);
        });
      }
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    if (gridRef.current) {
      gridRef.current.api.setFilterModel({});
    }
  };

  const handleLoadAll = async () => {
    setLoadingAll(true);
    try {
      await onLoadAll();
    } finally {
      setLoadingAll(false);
    }
  };

  const handleDownload = async () => {
    if (!downloadUrl) return;

    setDownloading(true);
    try {
      // Get the current filtered and sorted data from AG Grid
      const displayedData = [];
      gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
        displayedData.push(node.data);
      });

      // Prepare the payload with userId and loadAll
      const payload = {
        data: displayedData,
        fields: selectedFields,
        userId: userId,
        loadAll: loadAll || false
      };

      console.log('Download payload:', { 
        recordCount: displayedData.length, 
        fields: selectedFields.length,
        userId: userId,
        loadAll: loadAll 
      });

      // Call the API with POST method and send the data
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/schedules/download/generate`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      
      // Get filename from Content-Disposition header
      let downloadFileName = 'flight_data.xlsx';
      const contentDisposition = response.headers.get('content-disposition'); // ✅ fix header casing

      if (contentDisposition) {
        // Extract filename
        const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i);

        if (fileNameMatch && fileNameMatch[1]) {
          downloadFileName = fileNameMatch[1];
        }
      }
      console.log('Downloading file:', downloadFileName);

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      console.log('Download completed:', downloadFileName);
      
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const sumTime = (arr, field) => {
    let totalMinutes = arr.reduce((sum, row) => {
      const value = row[field];
      if (!value) return sum;

      const match = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(value);
      if (match) {
        const hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        return sum + hours * 60 + minutes;
      }
      return sum;
    }, 0);

    const hh = Math.floor(totalMinutes / 60);
    const mm = totalMinutes % 60;
    return `${hh}:${mm.toString().padStart(2, '0')}`;
  };

  const totalsRow = useMemo(() => {
    const totals = {};

    // numeric columns
    numericFields.forEach((field) => {
      totals[field] = data.reduce(
        (sum, row) => sum + Number(row[field] || 0),
        0
      );
    });

    // time columns
    timeFields.forEach((field) => {
      if (field === "flight_time") {
        totals[field] = sumTime(data, field);
      }
    });

    // add "Total" label in one of the text fields
    const labelField = selectedFields.find(f => !numericFields.includes(f));

    if (labelField) {
      totals[labelField] = "Total";
    } else if (selectedFields.length > 0) {
      totals[selectedFields[0]] = "Total";
    }

    // return row only if we have more than 1 key
    return Object.keys(totals).length > 1 ? [totals] : undefined;
  }, [data, numericFields, timeFields, selectedFields]);

  // Enhanced column definitions preserving API field order
  const columnDefs = useMemo(() => {
    let orderedFields = [];

    if (data && data.length > 0) {
      // API response order (LinkedHashMap preserves insertion order)
      const apiFieldOrder = Object.keys(data[0]);
      orderedFields = apiFieldOrder.filter(field => selectedFields.includes(field));
    } else {
      // Fallback to selectedFields order
      orderedFields = [...selectedFields];
    }

    return orderedFields.map(field => {
      const baseConfig = {
        headerName: fieldLabels[field] || field,
        field,
        sortable: true,
        resizable: true,
        headerClass: 'ag-header-cell-centered',
        cellClass: 'ag-cell-centered',
        valueFormatter: numericFields.includes(field)
          ? params => (params.value != null ? Number(params.value).toLocaleString() : '')
          : undefined,
      };

      if (numericFields.includes(field)) {
        return {
          ...baseConfig,
          filter: 'agNumberColumnFilter',
          floatingFilter: true,
          filterParams: {
            debounceMs: 200,
            suppressAndOrCondition: true,
          }
        };
      } else {
        return {
          ...baseConfig,
          filter: 'agTextColumnFilter',
          floatingFilter: true,
          filterParams: {
            debounceMs: 200,
            suppressAndOrCondition: true,
            defaultOption: 'contains',
          }
        };
      }
    });
  }, [data, selectedFields, numericFields]);

  const defaultColDef = {
    flex: 1,
    minWidth: 100,
  };

  const statusBar = useMemo(() => ({
    statusPanels: [
      { statusPanel: 'agTotalRowCountComponent', align: 'left' },
      { statusPanel: 'agAggregationComponent', align: 'right' },
    ],
  }), []);

  // Add CSS to hide pagination text
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .ag-paging-panel .ag-paging-row-summary-panel {
        display: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-light">
        {/* Main header section - mobile responsive */}
        <div className="row align-items-start mb-3">
          {/* Left side - Title and stats */}
          <div className="col-12 col-lg-6 mb-3 mb-lg-0">
            <h6 className="mb-2">Flight Results</h6>
            <div className="d-flex flex-column">
              <small className="text-muted mb-1">
                Showing: {returnedRecords?.toLocaleString() || data.length.toLocaleString()} flights
              </small>
              {totalRecords && (
                <small className="text-muted">
                  Total Available: {totalRecords.toLocaleString()} flights
                </small>
              )}
            </div>
          </div>

          {/* Right side - Action buttons */}
          <div className="col-12 col-lg-6">
            <div className="d-flex flex-column flex-lg-row gap-2 align-items-lg-center justify-content-lg-end">
              
              {/* Download Button */}
              {downloadUrl && userId && (
                <div className="d-flex flex-column align-items-center align-items-lg-end">
                  <button
                    className="btn btn-success btn-sm text-center"
                    onClick={handleDownload}
                    disabled={downloading || loading}
                    title="Download Excel file"
                    style={{ minWidth: '180px', whiteSpace: 'nowrap' }}
                  >
                    {downloading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                        Downloading...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-download me-1"></i>
                        Download Current View 
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Load All / Reset Controls with status above button on desktop */}
              {isLimited && hasMore && !loadAll && (
                <div className="d-flex flex-column align-items-center align-items-lg-end">
                  {/* Status text above button for both mobile and desktop */}
                  <div className="mb-1">
                    <small className="text-warning fw-bold text-center text-lg-end">
                      Limited to {limit > 0 ? limit.toLocaleString() : returnedRecords?.toLocaleString()} records
                    </small>
                  </div>
                  
                  <button
                    className="btn btn-warning btn-sm text-center"
                    onClick={handleLoadAll}
                    disabled={loadingAll || loading}
                    style={{ maxWidth: '180px' }}
                  >
                    {loadingAll ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Loading All...
                      </>
                    ) : (
                      `Load All ${totalRecords?.toLocaleString()} Records`
                    )}
                  </button>
                </div>
              )}

              {loadAll && (
                <div className="d-flex flex-column align-items-center align-items-lg-end">
                  {/* Status text above button for both mobile and desktop */}
                  <div className="mb-1">
                    <small className="text-success fw-bold text-center text-lg-end">
                      All {totalRecords?.toLocaleString()} records loaded
                    </small>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filter Status Display - updated via ref to prevent re-renders */}
        <div ref={filterDisplayRef} style={{ display: 'none' }} className="mb-2">
          <small className="text-info">
            <i className="fas fa-filter me-1"></i>
            <span className="filter-count"></span>
          </small>
          <div className="mt-1 filter-badges"></div>
        </div>
      </div>

      <div className="card-body p-0">
        <div className="ag-theme-alpine" style={{ width: '100%', height: '600px' }}>
          <AgGridReact
            ref={gridRef}
            rowData={data}
            onGridReady={onGridReady}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            statusBar={statusBar}
            pinnedBottomRowData={totalsRow}
            onFilterChanged={onFilterChanged}
            pagination={true}
            paginationPageSize={loadAll ? 50 : 10}
            paginationPageSizeSelector={[10, 25, 50, 100]}
            domLayout="normal"
            suppressPaginationPanel={false}
            paginationAutoPageSize={false}
          />
        </div>
      </div>

      {/* Loading overlay */}
      {(loading || loadingAll) && (
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-white bg-opacity-75" style={{ zIndex: 1000 }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-2" role="status"></div>
            <div className="fw-bold">
              {loadingAll ? 'Loading all records...' : 'Loading...'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(DataTable);